import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ReloadOutlined,
  CloseOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Grid } from '@lp/ds-next';
import { Empty, Flex, Space, Tag, Typography } from 'antd';
import { ColumnProps } from 'antd/es/table';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import useServiceLevelsStore, {
  IServiceLevelEntry,
} from '../../serviceLevels/hooks/useServiceLevelsStore';
import GenerateBillingReport from '../components/GenerateBillingReport';
import useGetShipments from '../hooks/useGetShipments';
import useShipmentsStore, {
  createEmptyShipmentSearchTerms,
  TShipmentsSortBy,
} from '../hooks/useShipmentsStore';
import { CONTRACT_STATES, TContractState } from '../models/shipment';
import BillingServerSearch from '@/components/BillingServerSearch';
import InspectedIcon from '@/components/icons/InspectedIcon';
import PageTitle from '@/components/layout/PageTitle';
import PillButton from '@/components/PillButton';
import ServerSearch from '@/components/ServerSearch';
import StyledTable from '@/components/tables/StyledTable';
import TablesTotals from '@/components/TablesTotals';
import { scopes } from '@/config';
import { useGetLitigations } from '@/features/gdpr/litigation/api/getLitigations';
import { LitigationTag } from '@/features/gdpr/litigation/components/LitigationTag';
import { useGetListInspection } from '@/features/securityCheck/api/getInspectionList';
import { useServiceLevels } from '@/features/serviceLevels';
import {
  getCurrentPage,
  getOffset,
  parseAntdSorter,
} from '@/helpers/antdTable';
import useAntdColumns from '@/hooks/useAntdColumns';
import useAppState from '@/hooks/useAppState';
import useWindowSize from '@/hooks/useWindowSize';
import { ShipmentsTotalContext } from '@/shared/providers/shipmentsTotalProvider';

const SHIPMENTS_SEARCH_FIELDS = [
  { key: 'id', placeholder: 'Shipment-ID' },
  { key: 'boxId', placeholder: 'Product-ID' },
  { key: 'lpui', placeholder: 'LPUI' },
  { key: 'partnerId', placeholder: 'Partnership name' },
  { key: 'reference', placeholder: 'Shipment name' },
  { key: 'recipientLastName', placeholder: 'Recipient Last Name' },
  { key: 'recipientEmail', placeholder: 'Recipient Email' },
  { key: 'carrierTrackingCode', placeholder: 'Carrier Reference Number' },
];

const Wrapper = styled.div<{ width?: string; mb?: string }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: ${({ mb }) => (mb ? mb : 0)};
  width: ${({ width }) => width};
`;

const { Text } = Typography;

const ShipmentsListView = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [billingMode, setBillingMode] = useState<boolean>(false);
  const [billingWindow, setBillingWindow] = useState([]);

  // 1. Generic hooks
  const [, setSearchParams] = useSearchParams();

  // Capture URL filter params synchronously on first render.
  // Using a lazy useState initializer (not useEffect) ensures useGetShipments
  // fires its very first request with the correct filter, avoiding a race
  // condition where an initial unfiltered response could overwrite the filtered one.
  const [urlInitTerms, setUrlInitTerms] = useState<{
    boxId?: string;
    partnerId?: string;
  } | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const boxId = params.get('boxId') ?? undefined;
    const partnerId = params.get('partnerId') ?? undefined;

    return boxId || partnerId ? { boxId, partnerId } : null;
  });

  const userScopes = useAppState((state) => state.scopes);
  const sTotal = useContext(ShipmentsTotalContext);
  const refreshTimeout = useRef<NodeJS.Timeout | null>(null);
  const loading = useShipmentsStore((state) => state.loading);
  const pageSize = useShipmentsStore((state) => state.pageSize);
  const offset = useShipmentsStore((state) => state.offset);
  const total = useShipmentsStore((state) => state.total);
  const defaultSort = useShipmentsStore((state) => state.defaultSort);
  const dispatch = useShipmentsStore((state) => state.dispatch);
  const searchTerms = useShipmentsStore((state) => state.searchTerms);
  const rts = useShipmentsStore((state) => state.rts);
  const incidentFilter = useShipmentsStore((state) => state.incidentFilter);
  const contractStatus = useShipmentsStore((state) => state.contractStatus);
  const windowSize = useWindowSize();
  const { result: serviceLevelsList } = useServiceLevels();
  const pagination = {
    current: getCurrentPage(pageSize, offset),
    pageSize,
    total,
    showSizeChanger: true,
  };

  const setServiceLevels = useServiceLevelsStore(
    (state) => state.setServiceLevels
  );
  useGetShipments(urlInitTerms ?? undefined);

  // Sync URL init terms into the Zustand store so the ServerSearch input
  // reflects the active filter, then hand over ownership to the store.
  useEffect(() => {
    if (!urlInitTerms) return;

    const nextSearchTerms = createEmptyShipmentSearchTerms();
    if (urlInitTerms.boxId) nextSearchTerms.boxId = urlInitTerms.boxId;
    if (urlInitTerms.partnerId)
      nextSearchTerms.partnerId = urlInitTerms.partnerId;

    dispatch({
      type: 'overrideSearchContext',
      args: { searchTerms: nextSearchTerms },
    });

    // Clean the URL so a manual refresh doesn't re-apply the filter
    setSearchParams({}, { replace: true });
    // Clear the override — the store is now the source of truth.
    // The query params are identical, so no new HTTP request fires.
    setUrlInitTerms(null);
    // Reason: effect runs once on mount — dispatch, setSearchParams and setUrlInitTerms are stable references
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rowData = useShipmentsStore((state) => state.rowData);

  const { data } = useGetLitigations({
    shipmentIds: rowData.map(({ id }) => id),
    enabled: rowData.length > 0,
  });

  const { data: inspectionData } = useGetListInspection({
    shipmentIds: rowData.map(({ id }) => id),
    enabled: rowData.length > 0,
  });

  if (serviceLevelsList.state === 'success') {
    const mappedServiceLevels: Array<IServiceLevelEntry> =
      serviceLevelsList.resource.items.map((level) => ({
        id: level.id,
        name: level.name,
      }));
    setServiceLevels(mappedServiceLevels);
    rowData.forEach((item) => {
      serviceLevelsList.resource.items.forEach((elt) => {
        if (elt.id === item.serviceLevel) {
          item.serviceLevel = elt.name;
        }
      });
    });
  }

  const handleRefresh = useCallback(() => {
    if (refreshTimeout.current) {
      clearTimeout(refreshTimeout.current);
    }
    refreshTimeout.current = setTimeout(() => {
      dispatch({
        type: 'refreshTable',
      });
    }, 100);
  }, [dispatch]);

  const handleTableChange = useCallback(
    (pagination, filters, sorter) => {
      if (refreshTimeout.current) {
        clearTimeout(refreshTimeout.current);
      }

      const [sortField, sortOrder] = parseAntdSorter(sorter);
      dispatch({
        type: 'updateSorting',
        args: { sortBy: sortField as TShipmentsSortBy, order: sortOrder },
      });

      dispatch({
        type: 'updatePagination',
        args: {
          offset: getOffset(pagination.pageSize, pagination.current),
          pageSize: pagination.pageSize,
        },
      });
      // Keep name for a functional logic
      const contractStatusFilters = isArray(get(filters, 'contractState'))
        ? filters.contractState
        : null;

      dispatch({
        type: 'updateFilters',
        args: {
          contractStatus: contractStatusFilters as TContractState[] | null,
          deliveryStatus: filters.deliveryStatus || null,
          incidentFilter: filters.incidents || null,
        },
      });

      refreshTimeout.current = setTimeout(() => {
        dispatch({ type: 'refreshTable' });
        refreshTimeout.current = null;
      }, 100);
    },
    [dispatch]
  );

  const columnKeys = [
    'shipmentId',
    'updatedAt',
    'reference',
    'boxSerialNumber',
    'contractState',
    'recipientAndSender',
    'carrierTrackingCode',
    'carrierCode',
    'deliveryStatus',
    'incidents',
  ];

  const onSelectChange = useCallback(
    (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    [setSelectedRowKeys]
  );

  const rowSelection = useMemo(
    () =>
      billingMode
        ? {
            selectedRowKeys,
            onChange: onSelectChange,
          }
        : null,
    [selectedRowKeys, onSelectChange, billingMode]
  );

  if (userScopes.includes(scopes.MANAGE_BILLING)) {
    columnKeys.push('isBillable');
  }

  const litigationsByShipmentId = useMemo(() => {
    if (!data) return {};

    return data.reduce((acc, item) => {
      if (item.status !== 'CANCELED') {
        acc[item.shipmentId] = item;
      }

      return acc;
    }, {});
  }, [data]);

  const inpectionsByShipmentId = useMemo(() => {
    if (!inspectionData) {
      return {};
    }

    return inspectionData.reduce((acc, item) => {
      if (item.status !== 'CANCELED') {
        acc[item.shipmentId] = item;
      }

      return acc;
    }, {});
  }, [inspectionData]);

  // Display contractStatus filter values
  const contractStateFilteredValue = useMemo(() => {
    if (contractStatus && contractStatus.length > 0) {
      return contractStatus;
    }

    return null;
  }, [contractStatus]);

  const columns: ColumnProps<any>[] = useAntdColumns({
    columnsKeys: columnKeys,
    addDefaultColumns: false,

    columnsSpecialProps: {
      contractState: {
        filters: CONTRACT_STATES.map((state) => ({
          text: state,
          value: state,
        })),
        filterMultiple: true,
        filteredValue: contractStateFilteredValue,
        width: '6rem',
      },
      reference: {
        width: '10rem',
      },
      deliveryStatus: {
        filters: [
          { label: 'RTS', value: true },
          { label: 'Not RTS', value: false },
        ].map((d) => ({
          text: d.label,
          value: d.value,
        })),
        filterMultiple: false,
        width: '8rem',
        title: 'Return To Sender',
        filteredValue: rts ? [rts] : null,
        render: (_, record) =>
          record.deliveryStatus === 'RETURN_TO_SENDER' ? (
            <Tag color="#FFF4F0">
              <WarningOutlined style={{ color: '#9A4F25', fontSize: '1rem' }} />
              <Text
                style={{
                  color: '#9A4F25',
                  fontWeight: '500',
                  fontSize: '0.75rem',
                }}
              >
                Return to sender
              </Text>
            </Tag>
          ) : null,
      },
      incidents: {
        title: 'Incidents',
        filters: [
          { label: 'Litigation solved', value: 'SOLVED' },
          { label: 'Litigation', value: 'FLAGGED' },
          { label: 'Inspected', value: 'INSPECTED' },
        ].map((d) => ({
          text: d.label,
          value: d.value,
        })),
        filterMultiple: false,
        filteredValue: incidentFilter ? [incidentFilter] : null,
        width: '8rem',
        render: (_, record) => {
          const litigation = litigationsByShipmentId[record.id];
          const inspected = inpectionsByShipmentId[record.id];
          const isInspectionValid =
            inspected &&
            (inspected.status === 'USED' || inspected.status === 'EXPIRED') &&
            (inspected.startedAt || inspected.endedAt);

          const tags: React.ReactNode[] = [];
          if (isInspectionValid) {
            tags.push(
              <Tag
                key="inspected"
                color="#91D5FF"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
                icon={<InspectedIcon color="#1890FF" />}
              >
                <Text
                  style={{
                    color: '#1890FF',
                    fontWeight: '500',
                    fontSize: '0.75rem',
                  }}
                >
                  Inspected
                </Text>
              </Tag>
            );
          }

          if (litigation) {
            tags.push(
              <LitigationTag
                key="litigation"
                litigationState={litigation.status}
              />
            );
          }

          return tags.length > 0 ? (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {tags}
            </div>
          ) : null;
        },
      },
    },
    defaultSort: defaultSort,
    eyeLinkProps: { to: '/shipments' },
  });

  //hack to have all rows selected by default
  useEffect(() => {
    if (billingMode) {
      const newSelection: string[] = [];
      rowData.forEach((row) => {
        newSelection.push(row.id);
      });
      setSelectedRowKeys(newSelection);
    }
  }, [rowData, billingMode]);

  return (
    <Grid container justifyContent="center" spacing={3}>
      <Grid item mobile={11}>
        <PageTitle title="SHIPMENTS" />
      </Grid>
      <Grid item mobile={11}>
        <Flex vertical>
          <Flex gap={8} justify="flex-end">
            <Wrapper>
              {userScopes.includes(scopes.MANAGE_BILLING) && (
                <GenerateBillingReport
                  billingMode={billingMode}
                  setBillingMode={setBillingMode}
                  billingWindow={billingWindow}
                  data={rowData}
                  selectedRows={selectedRowKeys}
                />
              )}
            </Wrapper>
          </Flex>
          {!billingMode && (
            <ServerSearch
              fields={SHIPMENTS_SEARCH_FIELDS}
              dispatch={dispatch}
              storeSearchTerms={searchTerms}
              extraButtons={
                <PillButton
                  onClick={handleRefresh}
                  type="default"
                  icon={<ReloadOutlined />}
                />
              }
            />
          )}
          {billingMode && (
            <Flex gap={8} style={{ alignSelf: 'flex-start' }}>
              <BillingServerSearch
                fields={[
                  { key: 'id', placeholder: 'Shipment-ID' },
                  { key: 'partnerId', placeholder: 'Partner-ID' },
                  { key: 'reference', placeholder: 'Shipment name' },
                  { key: 'activeState' },
                  { key: 'range' },
                ]}
                dispatch={dispatch}
                storeSearchTerms={searchTerms}
                setBillingWindow={setBillingWindow}
              />
              {/* fix me: hacky layout */}
              <Wrapper>
                <Flex vertical justify="space-around">
                  <Space style={{ height: '5px' }} />
                  <PillButton
                    onClick={handleRefresh}
                    type="default"
                    icon={<ReloadOutlined />}
                    data-testid="refresh-button"
                  />
                </Flex>
                <Space style={{ width: '10px' }} />
                <Flex vertical justify="space-around">
                  <Space style={{ height: '5px' }} />
                  <PillButton
                    onClick={() => setBillingMode(false)}
                    type="default"
                    icon={<CloseOutlined />}
                    data-testid="close-billing-mode-button"
                  />
                </Flex>
              </Wrapper>
            </Flex>
          )}
        </Flex>

        <StyledTable
          data-testid="shipment-list"
          rowKey="id"
          dataSource={rowData}
          rowSelection={rowSelection}
          rowClassName={(record) =>
            record.deletedAt > 0
              ? 'red-bg'
              : record.originalShipmentId
                ? 'green-bg'
                : ''
          }
          columns={columns}
          size="small"
          pagination={pagination}
          loading={loading}
          scroll={{ y: get(windowSize, 'height', 0) * 0.65 }}
          onChange={handleTableChange}
          footer={() => (
            <TablesTotals
              total={total}
              gTotal={sTotal}
              selectedItems={selectedRowKeys.length}
            />
          )}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  incidentFilter?.[0] === 'INSPECTED'
                    ? 'No “Inspected” shipments found.'
                    : 'No data'
                }
              />
            ),
          }}
        />
      </Grid>
    </Grid>
  );
};

export default ShipmentsListView;
